/**
 * What every motion group (render/motions/*.ts) is built from: the frame count, the body layout a
 * pose sets (`Rig`), the arms, easing, the stride, and the shapes of a group's poses, motions and arms.
 *
 * A leaf module: it imports nothing at run time, so any module may use it.
 */
import type { LimbArm, Pose } from './index';

export type NearArm =
  | 'hang'
  | 'forward'
  | 'guard'
  | 'raise'
  | 'sit'
  /** Fist in palm before the chest (抱拳), with the far hand. */
  | 'clasp'
  /** Placed against the body (rig figures; see `BODY_HANDS` in rig/skeleton.ts). */
  | BodyArm
  /** Drawn as a straight limb (`LIMB`): every motion group's `ARMS`. */
  | LimbArm;
/**
 * Arms placed against the body rather than reached toward a point: a hand on the hip, arms folded,
 * a hand to the chin or over the face, hands together in a clap (or just apart).
 */
export type BodyArm = 'hip' | 'fold' | 'chin' | 'face' | 'clap' | 'clapOpen' | 'overBack';
export type FarArm =
  | 'hang'
  | 'back'
  | 'fwd'
  | 'up'
  | 'clasp'
  /** The off hand holding the weapon reversed, the blade up along the outside of the forearm (motions/hold.ts). */
  | 'revArm'
  | Exclude<BodyArm, 'chin'>;

/** Body layout for one frame: everything a pose changes about the figure. */
export interface Rig {
  /** Upper-body drop (crouch, breathing, seated). */
  o: number;
  /** Upper-body shift forward (+) or back (−); legs stay planted. */
  lean: number;
  /**
   * Pitch of the spine in degrees, forward (+) or back (−), turning the upper body about the hips
   * (rig figures only): a bow, a lunge, a recoil. `lean` slides the body; this bends it.
   */
  tilt: number;
  headDx: number;
  /** Feet: forward offset and lift of the back (1) and front (2) foot. */
  f1: number;
  f2: number;
  l1: number;
  l2: number;
  /** Fabric lag: robes and hair trail back for positive values. */
  sway: number;
  armSwing: number;
  /** Robe hem lifted by the air, and how far it flares out. */
  hem: number;
  flare: number;
  arm: NearArm;
  /** The weapon arm on its way to another position, `armT` of the way there (an in-between frame). */
  armTo?: NearArm;
  armT?: number;
  /**
   * Which way the weapon hand swings round the shoulder between the two keys (+1 clockwise on
   * screen, −1 counter-clockwise): a blade turning about half a circle follows the hand that way
   * (over the head for an overhead cut) rather than the short way through the body.
   */
  armWay?: number;
  far: FarArm;
  face: 'idle' | 'atk' | 'block' | 'cast';
  closed: boolean;
  /** The upper back rounded forward (an elder's stoop), design px (rig/skeleton.ts `hunch`). */
  hunch?: number;
}

/**
 * Frames in every looping animation of a figure — walking, running, standing at ease, the ready
 * stance, putting a weapon away and drawing it: the project's one frame count, so every cycle is as
 * smooth as every other. (Single key poses — a strike, a block — are one frame each.)
 */
export const FRAMES = 14;
export const WALK_FRAMES = FRAMES;

/** Steps in putting a weapon away (and, backwards, drawing it). */
export const SHEATHE_FRAMES = FRAMES;

/** A pose's body layout at phase `ph`, laid over the at-rest defaults by `rigFor`. */
export type PoseRig<P extends string = string> = (ph: number, pose: P) => Partial<Rig>;

/**
 * A group's key poses by name (each told its own name, for a pose shared by several). Typed with
 * the group's own union of names, so `Pose` is made of names alone and nothing is inferred in a
 * circle through the poses' own code.
 */
export type PoseRigs<K extends string> = { [P in K]: PoseRig<P> };

/** A motion made of key poses (see `MOTIONS` in render/motions/index.ts). */
export interface MotionDef {
  id: string;
  keys: readonly Pose[];
  ease?: readonly Ease[];
  loop?: boolean;
  /**
   * A run that is one part of a longer loop (motions/idles.ts): the key before its first and after its last, for the
   * spline's tangents, so the curve runs on through the join.
   */
  before?: Pose;
  after?: Pose;
}

/** A near-arm pose drawn as a straight limb: hand position (relative to cx, before `o`) and blade angle. */
export interface LimbSpec {
  hand: [number, number];
  ang: number;
  front: boolean;
}

/**
 * Both feet through one stride at `t` (0–1 of the cycle): forward offset and lift of the back (1)
 * and front (2) foot, half a cycle apart, and how far the body sinks. A foot is planted for
 * `stance` of the cycle, sliding from `reach` ahead to `reach` behind at an even pace (so it stays
 * put on the ground as the body passes over it); the rest of the cycle it swings forward, eased,
 * lifting up to `lift` — highest early when `kick` is below 1 (the heel kicked up behind, then the
 * knee leading through, as in a run), at mid-swing at 1 (a walk's foot just clearing the ground).
 * The body sinks by up to `bob`, most when the planted foot is far from under the hips.
 */
export function gait(t: number, reach: number, stance: number, lift: number, kick = 0.7, bob = 3) {
  const leg = (p: number): [number, number] => {
    const q0 = ((p % 1) + 1) % 1;
    if (q0 < stance) return [reach - (2 * reach * q0) / stance, 0];
    const q = (q0 - stance) / (1 - stance);
    return [
      -reach + 2 * reach * (0.5 - 0.5 * Math.cos(Math.PI * q)),
      lift * Math.sin(Math.PI * Math.pow(q, kick)),
    ];
  };
  const [a, la] = leg(t);
  const [b, lb] = leg(t + 0.5);
  // The body rides on the planted foot nearest under the hips: highest as that foot passes under
  // them, lowest with both feet down and apart — twice a cycle, the same for either step.
  const planted =
    la === 0 && lb === 0 ? (Math.abs(a) < Math.abs(b) ? a : b) : la === 0 ? a : lb === 0 ? b : 0;
  return {
    f1: Math.round(b) || 0,
    l1: Math.round(lb),
    f2: Math.round(a) || 0,
    l2: Math.round(la),
    o: Math.round(bob * (planted / reach) ** 2),
  };
}

/**
 * How a motion moves between two of its keys: `in` gathers speed into the next key (a blow driving
 * into contact), `out` sets off fast and settles (a follow-through, a guard snapped up), `inOut`
 * eases away from one key and into the next (a body shifting its weight), `linear` keeps one pace.
 */
export type Ease = 'linear' | 'in' | 'out' | 'inOut';

/** `t` (0–1) of the way between two keys, eased; still 0 on the first key and 1 on the next. */
export function eased(kind: Ease, t: number): number {
  switch (kind) {
    case 'in':
      return t * t;
    case 'out':
      return 1 - (1 - t) * (1 - t);
    case 'inOut':
      return t * t * (3 - 2 * t);
    case 'linear':
      return t;
  }
}

/**
 * A looping motion's step `ph` of `FRAMES`, from its key positions spread evenly round the loop: each
 * number part of the way from one key to the next, the weapon arm on its way between theirs
 * (`armTo`), anything else (the other arm, the face) as the nearer key has it.
 */
export function keyed(keys: readonly Partial<Rig>[], ph: number): Partial<Rig> {
  const u = ((((ph % FRAMES) + FRAMES) % FRAMES) / FRAMES) * keys.length;
  const i = Math.floor(u);
  const t = u - i;
  const a = keys[i]!;
  const b = keys[(i + 1) % keys.length]!;
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(a)) {
    const w = (b as Record<string, unknown>)[k];
    if (typeof v === 'number' && typeof w === 'number') out[k] = v + (w - v) * t;
    else if (k === 'arm' && w !== undefined && w !== v && t > 0) {
      out.arm = v;
      out.armTo = w;
      out.armT = t;
    } else out[k] = t < 0.5 || w === undefined ? v : w;
  }
  return out as Partial<Rig>;
}
